<script lang="ts">
  import type { Maybe, Theme } from "../types";
  import { homePath, mapPath, morePath, placesPath, schedulePath } from "../support/links";
  import Icon from "./icon.svelte";
  import Sprite from "./sprite.svelte";
  import Locale from "./locale.svelte";

  export let segment: string;
  export let showHome = false;
  export let showSchedule = false;
  export let showMap = false;
  export let theme: Maybe<Theme> = null;

  $: homeSelectedIconSrc = theme?.homeSelectedIconSrc || theme?.homeIconSrc;
  $: homeIconSrc = segment === "/home" ? homeSelectedIconSrc : theme?.homeIconSrc;

  $: mapSelectedIconSrc = theme?.mapSelectedIconSrc || theme?.mapIconSrc;
  $: mapIconSrc = segment === "/map" ? mapSelectedIconSrc : theme?.mapIconSrc;

  $: infoSelectedIconSrc = theme?.infoSelectedIconSrc || theme?.infoIconSrc;
  $: infoIconSrc = segment === "/places" ? infoSelectedIconSrc : theme?.infoIconSrc;

  $: scheduleSelectedIconSrc = theme?.scheduleSelectedIconSrc || theme?.scheduleIconSrc;
  $: scheduleIconSrc = segment === "/schedule" ? scheduleSelectedIconSrc : theme?.scheduleIconSrc;

  $: moreSelectedIconSrc = theme?.moreSelectedIconSrc || theme?.moreIconSrc;
  $: moreIconSrc = segment === "/more" ? moreSelectedIconSrc : theme?.moreIconSrc;
</script>

<svelte:head>
  <link rel="preload" as="image" href={homeIconSrc} />
  <link rel="preload" as="image" href={homeSelectedIconSrc} />
  <link rel="preload" as="image" href={mapIconSrc} />
  <link rel="preload" as="image" href={mapSelectedIconSrc} />
  <link rel="preload" as="image" href={infoIconSrc} />
  <link rel="preload" as="image" href={infoSelectedIconSrc} />
  <link rel="preload" as="image" href={scheduleIconSrc} />
  <link rel="preload" as="image" href={scheduleSelectedIconSrc} />
  <link rel="preload" as="image" href={moreIconSrc} />
  <link rel="preload" as="image" href={moreSelectedIconSrc} />
</svelte:head>

<nav class="type-caption">
  {#if showHome}
    <a class:selected={segment === "/home"} href={homePath()}>
      {#if homeIconSrc}
        {#key homeIconSrc}
          <Icon src={homeIconSrc} fill="currentColor" />
        {/key}
      {:else}
        <Sprite icon="icon-home" />
      {/if}
      <Locale label="nav.home" />
    </a>
  {/if}

  {#if showMap}
    <a class:selected={segment === "/map"} href={mapPath()}>
      {#if mapIconSrc}
        {#key mapIconSrc}
          <Icon src={mapIconSrc} fill="currentColor" />
        {/key}
      {:else}
        <Sprite icon="icon-map" />
      {/if}
      <Locale label="nav.map" />
    </a>
  {/if}

  <a
    class:selected={segment === "/places" || segment === "/place-types/[id=slug]"}
    href={placesPath()}
  >
    {#if infoIconSrc}
      {#key infoIconSrc}
        <Icon src={infoIconSrc} fill="currentColor" />
      {/key}
    {:else}
      <Sprite icon="icon-info" />
    {/if}
    <Locale label="nav.info" />
  </a>

  {#if showSchedule}
    <a class:selected={segment === "/schedule"} href={schedulePath()}>
      {#if scheduleIconSrc}
        {#key scheduleIconSrc}
          <Icon src={scheduleIconSrc} fill="currentColor" />
        {/key}
      {:else}
        <Sprite icon="icon-today" />
      {/if}
      <Locale label="nav.today" />
    </a>
  {/if}

  <a class:selected={segment === "/more" || segment === "info"} href={morePath()}>
    {#if moreIconSrc}
      {#key moreIconSrc}
        <Icon src={moreIconSrc} fill="currentColor" />
      {/key}
    {:else}
      <Sprite icon="icon-more-vertical" />
    {/if}
    <Locale label="nav.more" />
  </a>
</nav>

<style>
  /**
   * Loosely follows https://material.io/components/app-bars-bottom#specs
   */
  nav {
    display: flex;
    justify-content: space-around;
    align-items: stretch;
    margin: 0 auto;
    max-width: var(--container-width);
    padding: 0 0 calc(0.75 * env(safe-area-inset-bottom, 0px));
    height: var(--nav-height);
    text-align: center;
  }

  a {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 8px;
    color: var(--theme-nav-icon, currentColor);
    display: flex;
    flex-direction: column;
    flex: 1 1;
    font: inherit;
    line-height: 1.75;
    justify-content: center;
    letter-spacing: inherit;
    opacity: 1;
    padding: 0;
    margin: 4px;
    text-decoration: none;
    text-transform: unset;
    transition: 0.15s all;
  }

  .selected {
    background: var(--theme-nav-selected-bg);
    color: var(--theme-nav-selected-icon);
    opacity: 1;
  }
</style>
